import { IS_ALFAJR } from '../constants/general';
import {
  getSeoStatusFromScore,
  getSeoStatusClass,
  SEO_SCORE_FILTER_OPTIONS,
  slugifyCategory,
  slugifyUrlSlug,
  resolveSeoSchemaField,
  generateDefaultFaqSchema,
  SEO_SITE_HOME,
  SEO_ORDER_BASE,
} from './seoHelpers';

const SEO_BASE_URL = IS_ALFAJR
  ? 'https://alfajr.qa'
  : 'https://ablemartonline.com/qatar';

export { SEO_SCORE_FILTER_OPTIONS, getSeoStatusFromScore, getSeoStatusClass };

export const buildCategorySeoPreviewUrl = (item, urlSlug = '') => {
  const isSubcategory = item?.type === 'subcategory';
  const resolvedSlug = slugifyUrlSlug(urlSlug || item?.seoData?.urlSlug || '');
  const categoryId = item?.parentCategoryId || item?._id || '';

  if (isSubcategory) {
    const parent = slugifyCategory(item?.parentCategory || 'category');
    const subSegment = resolvedSlug || slugifyUrlSlug(item?.title || '');
    return `${SEO_BASE_URL}/order/${parent}/c/${categoryId}?subCategory=${subSegment}`;
  }

  const categorySegment = resolvedSlug || slugifyCategory(item?.title || 'category');
  return `${SEO_BASE_URL}/order/${categorySegment}/c/${categoryId}`;
};

const formatSchemaJson = (obj) => JSON.stringify(obj, null, 2);

export const generateCategorySchemaMarkup = (item) => {
  const seoData = item?.seoData || {};
  const title = seoData.pageTitle || item?.title || '';
  const description = seoData.pageDescription || item?.description || '';
  const pageUrl = buildCategorySeoPreviewUrl(item, seoData.urlSlug || '');

  return formatSchemaJson({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: pageUrl,
    ...(item?.image ? { image: item.image } : {}),
  });
};

export const generateCategoryBreadcrumbSchema = (item) => {
  const seoData = item?.seoData || {};
  const title = seoData.pageTitle || item?.title || '';
  const isSubcategory = item?.type === 'subcategory';
  const pageUrl = buildCategorySeoPreviewUrl(item, seoData.urlSlug || '');

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SEO_SITE_HOME,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'All Categories',
      item: `${SEO_ORDER_BASE}/order`,
    },
  ];

  if (isSubcategory) {
    const parentUrl = buildCategorySeoPreviewUrl(
      {
        ...item,
        type: 'category',
        title: item?.parentCategory || '',
        parentCategory: '',
      },
      '',
    );
    itemListElement.push(
      {
        '@type': 'ListItem',
        position: 3,
        name: item?.parentCategory || 'Category',
        item: parentUrl,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: title,
        item: pageUrl,
      },
    );
  } else {
    itemListElement.push({
      '@type': 'ListItem',
      position: 3,
      name: title,
      item: pageUrl,
    });
  }

  return formatSchemaJson({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  });
};

export const createEmptyContentSection = () => ({ title: '', paragraph: '' });

export const initializeCategorySeoFormData = (item) => {
  const seoData = item?.seoData || {};
  const title = item?.title || '';
  const pageTitle = seoData.pageTitle || title;
  const pageDescription = seoData.pageDescription || item?.description || '';

  const formItem = {
    ...item,
    seoData: {
      ...seoData,
      pageTitle,
      pageDescription,
    },
  };

  return {
    pageTitle,
    metaTitle: seoData.metaTitle || pageTitle,
    metaDescription: seoData.metaDescription || '',
    urlSlug: seoData.urlSlug || '',
    pageDescription,
    image: item?.image || '',
    imageAlt: seoData.imageAlt || title,
    contentSections: (seoData.contentSections || []).map((section) => ({
      title: section.title || '',
      paragraph: section.paragraph || '',
    })),
    categorySchemaMarkup: resolveSeoSchemaField(
      seoData.categorySchemaMarkup,
      () => generateCategorySchemaMarkup(formItem),
    ),
    breadCrumbSchema: resolveSeoSchemaField(
      seoData.breadCrumbSchema,
      () => generateCategoryBreadcrumbSchema(formItem),
    ),
    faqSchema: resolveSeoSchemaField(
      seoData.faqSchema,
      () => generateDefaultFaqSchema(),
    ),
  };
};

const hasValue = (value) => Boolean(value && String(value).trim());

const hasSchemaMarkup = (value) => hasValue(value) && String(value).trim().length > 10;

export const calculateCategorySeoScore = (formData = {}) => {
  const checks = [
    hasValue(formData.pageTitle),
    hasValue(formData.metaTitle),
    hasValue(formData.metaDescription),
    hasValue(formData.urlSlug),
    hasValue(formData.pageDescription),
    (formData.contentSections || []).some(
      (section) => hasValue(section.title) && hasValue(section.paragraph)
    ),
    hasSchemaMarkup(formData.categorySchemaMarkup),
    hasSchemaMarkup(formData.breadCrumbSchema),
    hasSchemaMarkup(formData.faqSchema),
    hasValue(formData.imageAlt),
  ];

  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
};

export const buildCategorySeoSavePayload = (item, formData) => ({
  categoryId: item._id,
  subCategoryId: item.subCategoryId || null,
  seoData: {
    pageTitle: formData.pageTitle?.trim() || '',
    metaTitle: formData.metaTitle?.trim() || '',
    metaDescription: formData.metaDescription?.trim() || '',
    urlSlug: slugifyUrlSlug(formData.urlSlug || ''),
    pageDescription: formData.pageDescription?.trim() || '',
    imageAlt: formData.imageAlt?.trim() || '',
    contentSections: (formData.contentSections || [])
      .map((section) => ({
        title: section.title?.trim() || '',
        paragraph: section.paragraph?.trim() || '',
      }))
      .filter((section) => section.title || section.paragraph),
    categorySchemaMarkup: formData.categorySchemaMarkup?.trim() || '',
    breadCrumbSchema: formData.breadCrumbSchema?.trim() || '',
    faqSchema: formData.faqSchema?.trim() || '',
  },
});

export const getCategorySeoScore = (item) => {
  if (typeof item?.seoScore === 'number') return item.seoScore;
  if (typeof item?.seoData?.seoScore === 'number') return item.seoData.seoScore;
  return calculateCategorySeoScore(initializeCategorySeoFormData(item));
};

export const getCategorySeoStatus = (item) => {
  if (item?.seoStatus) return item.seoStatus;
  return getSeoStatusFromScore(getCategorySeoScore(item));
};
